/**
 * Form UI Module
 */

export class FormUI {
    private originalButtonText: string = '';

    /**
     * Store original button text
     */
    storeOriginalButtonText(button: HTMLButtonElement | null): void {
        if (button) {
            const span = button.querySelector('span');
            this.originalButtonText = span ? span.textContent || '' : button.textContent || '';
        }
    }

    /**
     * Set loading state
     */
    setLoadingState(form: HTMLFormElement, button: HTMLButtonElement | null, loading: boolean): void {
        if (loading) {
            form.classList.add('opacity-60', 'pointer-events-none');
            if (button) {
                button.disabled = true;
                const span = button.querySelector('span');
                const loadingText = button.dataset.loadingText || 'Sending...';
                if (span) {
                    span.textContent = loadingText;
                } else {
                    button.textContent = loadingText;
                }
            }
        } else {
            form.classList.remove('opacity-60', 'pointer-events-none');
            if (button) {
                button.disabled = false;
                const span = button.querySelector('span');
                if (span) {
                    span.textContent = this.originalButtonText;
                } else {
                    button.textContent = this.originalButtonText;
                }
            }
        }
    }

    /**
     * Show field error
     */
    showFieldError(field: HTMLElement | null, message: string): void {
        if (!field) return;
        
        const wrapper = field.closest('.form-field');
        if (!wrapper) return;

        field.classList.add('border-red-500');
        
        const errorSpan = wrapper.querySelector('.form-error');
        if (errorSpan) {
            errorSpan.textContent = message;
            errorSpan.classList.remove('hidden');
        }
    }

    /**
     * Clear field error
     */
    clearFieldError(field: HTMLElement | null): void {
        if (!field) return;
        
        const wrapper = field.closest('.form-field');
        if (!wrapper) return;

        field.classList.remove('border-red-500');
        
        const errorSpan = wrapper.querySelector('.form-error');
        if (errorSpan) {
            errorSpan.textContent = '';
            errorSpan.classList.add('hidden');
        }
    }

    /**
     * Clear all errors
     */
    clearAllErrors(form: HTMLFormElement): void {
        form.querySelectorAll('.border-red-500').forEach(field => {
            field.classList.remove('border-red-500');
        });
        
        form.querySelectorAll('.form-error').forEach(error => {
            error.textContent = '';
            error.classList.add('hidden');
        });
        
        // Clear character count
        form.querySelectorAll('.char-counter').forEach(counter => {
            counter.remove();
        });
    }

    /**
     * Show message
     */
    showMessage(container: HTMLElement | null, message: string, type: 'success' | 'error'): void {
        if (!container) return;

        const typeClasses = {
            success: 'bg-redi-hellblau border-l-4 border-redi-gelb text-redi-blau',
            error: 'bg-redi-hellblau border-l-4 border-red-500 text-redi-blau'
        };

        container.innerHTML = `
            <div class="form-message ${typeClasses[type]} px-6 py-4" role="alert">
                <p class="text-base font-medium">${message}</p>
            </div>
        `;
        
        container.classList.remove('hidden');
    }

    /**
     * Update character count for textarea
     */
    updateCharacterCount(textarea: HTMLTextAreaElement | null, maxLength: number): void {
        if (!textarea) return;
        
        const currentLength = textarea.value.length;
        const wrapper = textarea.closest('.form-field');
        if (!wrapper) return;

        let counter = wrapper.querySelector('.char-counter');
        if (!counter) {
            counter = document.createElement('span');
            counter.className = 'char-counter text-sm text-redi-grau mt-1 block';
            wrapper.appendChild(counter);
        }

        counter.textContent = `${currentLength} / ${maxLength}`;
        
        if (currentLength > maxLength * 0.9) {
            counter.classList.add('text-redi-gelb');
        } else {
            counter.classList.remove('text-redi-gelb');
        }
    }
}